import React, { useState } from 'react';
import {
  MessageSquare,
  CheckCircle2,
  Phone,
  ShieldCheck,
  Zap,
  MapPin,
  Clock,
  ArrowRight,
  Database,
  Building2,
  ChevronDown,
  Calculator,
  Lock,
  Globe
} from 'lucide-react';
import { createWhatsAppLeadUrl, DISPLAY_PHONE_NUMBER } from '../utils/whatsapp';
import { CustomImage } from './CustomImage';

export type RegionalHubKey = 'nairobi' | 'mombasa' | 'nakuru' | 'kisumu' | 'meru';

interface RegionalHubViewProps {
  hubKey: RegionalHubKey;
  onOpenBooking: (serviceName?: string) => void;
  onOpenPaybill: (amount: number, packageTitle: string) => void;
}

interface HubDetail {
  key: RegionalHubKey;
  cityName: string;
  countyName: string;
  hubTagline: string;
  heroHeading: string;
  heroDescription: string;
  primaryService: string;
  localAddress: string;
  mapCoordinates: { lat: number; lng: number };
  featuredCaseStudy: {
    title: string;
    industry: string;
    result: string;
    description: string;
    imageUrl: string;
  };
  localizedFaqs: Array<{ question: string; answer: string }>;
}

const HUB_DATA: Record<RegionalHubKey, HubDetail> = {
  nairobi: {
    key: 'nairobi',
    cityName: 'Nairobi',
    countyName: 'Nairobi HQ',
    hubTagline: 'EAST AFRICA TECH & CORPORATE HQ HUB',
    heroHeading: 'High-Converting Web Design, Mobile Apps & Enterprise ERPs in Nairobi',
    heroDescription: 'Engineered in Nairobi for ambitious Kenyan brands. Get sub-second website packages, custom Laravel software, and automated M-Pesa cashier STK push integrations.',
    primaryService: 'Corporate Website Packages & Laravel ERP Systems',
    localAddress: 'Thinkers Arcade Tech Hub, Kilimani / CBD, Nairobi',
    mapCoordinates: { lat: -1.2921, lng: 36.8219 },
    featuredCaseStudy: {
      title: 'Commercial Fleet & Real Estate Operations Portal',
      industry: 'Commercial Real Estate & Logistics',
      result: 'Replaced 18 Excel Workbooks with Automated Cashier Reconciliation',
      description: 'Built a multi-branch web ERP connected directly to Safaricom Daraja API for automated rent reconciliation across 450 tenants.',
      imageUrl: '/images/custom_erp_dashboard_1790951335673.webp',
    },
    localizedFaqs: [
      {
        question: 'Can your Nairobi developers meet us face-to-face for project onboarding?',
        answer: 'Yes! Our lead software architects and UX designers are based in Nairobi and available for in-person technical discovery sessions at your offices or our Kilimani hub.',
      },
      {
        question: 'How fast can a corporate business website launch in Nairobi?',
        answer: 'Standard business website packages launch within 48 to 72 hours. Custom ERP software and mobile apps average 2 to 3 weeks.',
      },
    ],
  },
  mombasa: {
    key: 'mombasa',
    cityName: 'Mombasa',
    countyName: 'Mombasa & Coast Region',
    hubTagline: 'COASTAL E-COMMERCE, TOURISM & PORT LOGISTICS HUB',
    heroHeading: 'Top E-Commerce Website Design & Web Development in Mombasa',
    heroDescription: 'Scale your Coast business, resort, or port logistics firm with fast websites, automated M-Pesa online checkouts, and technical SEO in Nyali, Bamburi, and Mombasa Island.',
    primaryService: 'E-Commerce Website Development & Tourism Portals',
    localAddress: 'Thinkers Arcade Coast Hub, Nyali / Mombasa Island',
    mapCoordinates: { lat: -4.0435, lng: 39.6682 },
    featuredCaseStudy: {
      title: 'Coastal Hotel & Excursion Online Booking Platform',
      industry: 'Hospitality & Maritime Tourism',
      result: '140% Increase in Direct Direct-Booking Conversions',
      description: 'Launched a high-speed resort booking web app with real-time room availability, M-Pesa STK push, and card payment gateways.',
      imageUrl: '/images/nairobiwest_spa_hero.webp',
    },
    localizedFaqs: [
      {
        question: 'Do your e-commerce websites in Mombasa accept Lipa na M-Pesa STK Push?',
        answer: 'Absolutely. Every e-commerce shop comes integrated with Safaricom Daraja M-Pesa STK push, sending immediate payment prompts directly to customer phones upon checkout.',
      },
      {
        question: 'How do you optimize websites for Coast mobile internet speeds?',
        answer: 'We optimize all images to compressed WebP format and serve assets via KIXP-peered LiteSpeed cloud servers, ensuring instant loading even on 3G mobile networks in Mombasa.',
      },
    ],
  },
  nakuru: {
    key: 'nakuru',
    cityName: 'Nakuru',
    countyName: 'Nakuru & Rift Valley Hub',
    hubTagline: 'RIFT VALLEY AGRIBUSINESS & WHOLESALE HUB',
    heroHeading: 'Custom Website Design & Retail POS Software in Nakuru',
    heroDescription: 'Empowering Nakuru retailers, hardware distributors, and agricultural processors with custom websites, inventory ERP software, and Google Maps #1 local ranking.',
    primaryService: 'Retail POS Software & Agribusiness Portals',
    localAddress: 'Thinkers Arcade Rift Hub, Kenyatta Avenue, Nakuru',
    mapCoordinates: { lat: -0.3031, lng: 36.0800 },
    featuredCaseStudy: {
      title: 'Multi-Branch Wholesale & Agro-Chemical Inventory System',
      industry: 'Agribusiness & Hardware Distribution',
      result: 'Zero Inventory Discrepancies Across 4 Branches',
      description: 'Digitized manual receipt books into a central web ERP with real-time stock alerts and automated M-Pesa cashier balancing.',
      imageUrl: '/images/shantis_solar_ecommerce_1790951316846.webp',
    },
    localizedFaqs: [
      {
        question: 'Can you integrate KRA eTIMS with our Nakuru wholesale business software?',
        answer: 'Yes! Our custom web ERP systems connect directly with the KRA eTIMS API, automatically generating compliant fiscal tax receipts for every sale.',
      },
      {
        question: 'Is Lipa Pole Pole installment payment option available in Nakuru?',
        answer: 'Yes! All Nakuru website and software packages qualify for our Lipa Pole Pole milestone plan with a low 3,000/= initial deposit.',
      },
    ],
  },
  kisumu: {
    key: 'kisumu',
    cityName: 'Kisumu',
    countyName: 'Kisumu & Lake Region Hub',
    hubTagline: 'LAKE REGION HEALTHCARE & COMMERCIAL HUB',
    heroHeading: 'Website Developers & Android App Engineering in Kisumu',
    heroDescription: 'Delivering world-class websites, Android/iOS mobile apps, and hospital management software for growing businesses and organizations across Kisumu and Nyanza.',
    primaryService: 'Mobile App Development & Medical/Hotel Software',
    localAddress: 'Thinkers Arcade Kisumu Hub, Oginga Odinga Road, Kisumu',
    mapCoordinates: { lat: -0.0917, lng: 34.7680 },
    featuredCaseStudy: {
      title: 'Regional Specialist Hospital & Patient Booking App',
      industry: 'Healthcare & Clinical Services',
      result: 'Reduced Consultation Wait Times by 65%',
      description: 'Developed an offline-first mobile web app for doctor appointments, pharmacy stock management, and M-Pesa prescription billing.',
      imageUrl: '/images/app_development_showcase_1790953231741.webp',
    },
    localizedFaqs: [
      {
        question: 'Can you build Android mobile apps with M-Pesa integration in Kisumu?',
        answer: 'Yes, we engineer bespoke Android and iOS mobile applications using React Native/Flutter with full M-Pesa SDK integration and offline data sync.',
      },
      {
        question: 'Do you provide website maintenance and hosting support in Kisumu?',
        answer: 'Yes! Every website package includes 1 full year of LiteSpeed NVMe cloud web hosting, SSL certificate, and 24/7 technical support.',
      },
    ],
  },
  meru: {
    key: 'meru',
    cityName: 'Meru',
    countyName: 'Meru & Mt. Kenya Hub',
    hubTagline: 'MT. KENYA COMMERCE & SACCO HUB',
    heroHeading: 'Website Design & Digital Marketing Services in Meru',
    heroDescription: 'Get your Meru business calling and selling online. We build high-converting websites, manage targeted Facebook/Google Ads, and rank your brand #1 on Google Maps.',
    primaryService: 'Business Website Packages & Technical SEO',
    localAddress: 'Thinkers Arcade Mt. Kenya Hub, Meru Town',
    mapCoordinates: { lat: 0.0463, lng: 37.6559 },
    featuredCaseStudy: {
      title: 'Mt. Kenya Produce Sacco & Member Dividend Portal',
      industry: 'Agricultural Saccos & Credit Co-operatives',
      result: 'Automated Dividend Calculation for 3,200 Farmers',
      description: 'Engineered a secure web portal allowing farmers to check produce deliveries and receive instant M-Pesa dividend payouts.',
      imageUrl: '/images/hero_digital_infrastructure_1790951298995.webp',
    },
    localizedFaqs: [
      {
        question: 'How does digital marketing and website design help my Meru business?',
        answer: 'When local clients in Meru search for your services on Google, our technical SEO places your website at the top of local search results, driving direct phone calls and WhatsApp inquiries.',
      },
      {
        question: 'What is included in the KES 9,000 Starter Website Package in Meru?',
        answer: 'It includes custom mobile-responsive web design, free .co.ke or .com domain registration, 1 year NVMe hosting, WhatsApp click-to-chat integration, and SSL security.',
      },
    ],
  },
};

export const RegionalHubView: React.FC<RegionalHubViewProps> = ({
  hubKey,
  onOpenBooking,
  onOpenPaybill
}) => {
  const hub = HUB_DATA[hubKey] || HUB_DATA.nairobi;

  // State for interactive WhatsApp quote estimator
  const [calcService, setCalcService] = useState<'website' | 'app' | 'erp' | 'ai'>('website');
  const [calcBudget, setCalcBudget] = useState<number>(22000);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const handleWhatsAppClick = (packageTitle?: string, customService?: string) => {
    const targetService = customService || hub.primaryService;
    const url = createWhatsAppLeadUrl({
      region: hub.cityName as any,
      service: targetService,
      packageTitle,
      estimatedBudget: calcBudget.toLocaleString()
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-[#080d11] text-white min-h-screen">
      
      {/* 1. Hero Section (Optimized for Mobile Sub-0.5s FCP) */}
      <section className="relative overflow-hidden pt-12 pb-16 lg:pt-20 lg:pb-24 border-b border-slate-800 bg-gradient-to-b from-[#091724] via-[#080d11] to-[#080d11]">
        <div className="absolute inset-0 bg-grid-pattern-dark opacity-10 pointer-events-none" />
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-6 text-left">
              
              {/* Regional Kicker Tag */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/90 border border-[#c59b4c]/40 text-[#fcd34d] text-xs font-mono font-semibold">
                <MapPin className="w-3.5 h-3.5 text-[#c59b4c]" />
                <span>{hub.hubTagline} · {hub.countyName.toUpperCase()}</span>
              </div>

              {/* Localized H1 Headline */}
              <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white font-serif-heading leading-tight">
                {hub.heroHeading}
              </h1>

              {/* Subheadline */}
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-sans max-w-2xl">
                {hub.heroDescription}
              </p>

              {/* Local Trust Badges */}
              <div className="flex flex-wrap gap-4 text-xs font-mono text-slate-300 pt-2">
                <span className="flex items-center gap-1.5 text-emerald-400">
                  <CheckCircle2 className="w-4 h-4" /> M-Pesa STK Push Ready
                </span>
                <span className="flex items-center gap-1.5 text-[#fcd34d]">
                  <ShieldCheck className="w-4 h-4 text-[#c59b4c]" /> KRA eTIMS Middleware
                </span>
                <span className="flex items-center gap-1.5 text-blue-400">
                  <Zap className="w-4 h-4 text-blue-400" /> KIXP LiteSpeed NVMe
                </span>
              </div>

              {/* Dual WhatsApp CTA Buttons */}
              <div className="pt-4 flex flex-wrap gap-4 items-center">
                <button
                  onClick={() => handleWhatsAppClick('Hero Direct Lead')}
                  className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-xl transition-all hover:scale-[1.02] cursor-pointer"
                >
                  <MessageSquare className="w-5 h-5 text-white" />
                  <span>WhatsApp Quote ({hub.cityName})</span>
                </button>

                <button
                  onClick={() => onOpenBooking(`${hub.cityName} Architecture Session`)}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#c59b4c] hover:bg-[#b08535] text-slate-950 font-bold text-sm shadow-md transition-all cursor-pointer"
                >
                  <span>Book Strategy Call</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {/* Phone Fast Line */}
              <p className="text-xs font-mono text-slate-400 pt-1">
                Direct Hotline: <a href="tel:+254114646341" className="text-white hover:text-[#c59b4c] font-bold">{DISPLAY_PHONE_NUMBER}</a> (Response within 10 minutes)
              </p>
            </div>

            {/* Right Showcase Card */}
            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-slate-700/80 bg-[#0d1622]/90 backdrop-blur-xl shadow-2xl p-6 text-left space-y-5">
                
                <div className="flex items-center justify-between pb-3 border-b border-slate-800 text-xs">
                  <div className="flex items-center gap-2 font-mono text-slate-200">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span>{hub.cityName} Digital Office</span>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-[#fcd34d] bg-[#c59b4c]/15 border border-[#c59b4c]/30 px-2 py-0.5 rounded-full">
                    LIPA POLE POLE
                  </span>
                </div>

                {/* Local Case Study Preview Image */}
                <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-950">
                  <CustomImage
                    src={hub.featuredCaseStudy.imageUrl}
                    alt={hub.featuredCaseStudy.title}
                    width={560}
                    height={280}
                    className="w-full h-48 object-cover"
                    priority={true}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
                  <span className="absolute bottom-3 left-3 bg-black/80 backdrop-blur-md px-3 py-1 rounded-lg border border-white/10 text-xs text-white font-medium">
                    {hub.featuredCaseStudy.industry}
                  </span>
                </div>

                <div>
                  <h3 className="text-base font-bold text-white font-serif-heading">
                    {hub.featuredCaseStudy.title}
                  </h3>
                  <p className="text-xs text-emerald-400 font-mono mt-1">
                    ✓ {hub.featuredCaseStudy.result}
                  </p>
                  <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                    {hub.featuredCaseStudy.description}
                  </p>
                </div>

                <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-xs">
                  <span className="text-slate-400">Address: {hub.localAddress}</span>
                  <button
                    onClick={() => handleWhatsAppClick('Case Study Inquiry')}
                    className="text-[#c59b4c] hover:underline font-bold"
                  >
                    View Specs &rarr;
                  </button>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 2. Transparent Packages & Pricing Table for Region */}
      <section className="py-16 bg-[#080d11] border-b border-slate-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center space-y-10">
          
          <div className="space-y-3 max-w-3xl mx-auto">
            <span className="text-xs font-mono text-[#c59b4c] font-semibold tracking-wider uppercase">
              TRANSPARENT REGIONAL PACKAGES
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-serif-heading">
              Website &amp; Software Pricing in {hub.cityName}
            </h2>
            <p className="text-slate-300 text-sm sm:text-base">
              No hidden fees. Every package includes free .co.ke domain, 1-Year LiteSpeed NVMe cloud hosting, SSL security, and Lipa Pole Pole milestone plans.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            
            {/* Package 1: Starter */}
            <div className="rounded-2xl border border-slate-800 bg-[#0d1622] p-6 space-y-6 flex flex-col justify-between hover:border-slate-700 transition-colors">
              <div className="space-y-4">
                <span className="text-xs font-mono text-slate-400">STARTER WEB PACKAGE</span>
                <h3 className="text-xl font-bold text-white">Business Website</h3>
                <p className="text-2xl font-mono font-extrabold text-[#c59b4c]">KES 9,000</p>
                <p className="text-xs text-slate-400">Deposit: 3,000/= deposit to start (Lipa Pole Pole)</p>
                <ul className="space-y-2.5 text-xs text-slate-300 pt-2 border-t border-slate-800">
                  <li className="flex items-center gap-2">✓ 1-5 Custom Responsive Pages</li>
                  <li className="flex items-center gap-2">✓ Free .co.ke or .com Domain</li>
                  <li className="flex items-center gap-2">✓ 1 Year NVMe Cloud Hosting</li>
                  <li className="flex items-center gap-2">✓ WhatsApp Click-to-Chat Button</li>
                  <li className="flex items-center gap-2">✓ Google Maps #1 Local Ranking</li>
                </ul>
              </div>
              <button
                onClick={() => handleWhatsAppClick('Starter Website Package - KES 9,000')}
                className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white font-bold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
              >
                <MessageSquare className="w-4 h-4 text-emerald-400" />
                <span>Order via WhatsApp (9K)</span>
              </button>
            </div>

            {/* Package 2: Corporate Business */}
            <div className="rounded-2xl border-2 border-[#c59b4c] bg-[#0d1622] p-6 space-y-6 flex flex-col justify-between relative shadow-2xl">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c59b4c] text-slate-950 font-mono font-extrabold text-[10px] px-3 py-0.5 rounded-full">
                MOST POPULAR IN {hub.cityName.toUpperCase()}
              </span>
              <div className="space-y-4">
                <span className="text-xs font-mono text-[#fcd34d]">CORPORATE &amp; M-PESA</span>
                <h3 className="text-xl font-bold text-white">Pro Corporate &amp; M-Pesa</h3>
                <p className="text-2xl font-mono font-extrabold text-[#fcd34d]">KES 22,000</p>
                <p className="text-xs text-slate-400">Deposit: 8,000/= deposit to start</p>
                <ul className="space-y-2.5 text-xs text-slate-200 pt-2 border-t border-slate-800">
                  <li className="flex items-center gap-2">✓ Up to 12 Enterprise Pages</li>
                  <li className="flex items-center gap-2">✓ Safaricom M-Pesa Paybill / Till Checkout</li>
                  <li className="flex items-center gap-2">✓ Automated Invoice PDF Generator</li>
                  <li className="flex items-center gap-2">✓ Corporate Email Addresses (name@company.co.ke)</li>
                  <li className="flex items-center gap-2">✓ KRA eTIMS Middleware Ready</li>
                </ul>
              </div>
              <button
                onClick={() => handleWhatsAppClick('Pro Corporate & M-Pesa Package - KES 22,000')}
                className="w-full py-3 rounded-xl bg-[#c59b4c] hover:bg-[#b08535] text-slate-950 font-bold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Order Pro Package (22K)</span>
              </button>
            </div>

            {/* Package 3: Custom ERP & Software */}
            <div className="rounded-2xl border border-slate-800 bg-[#0d1622] p-6 space-y-6 flex flex-col justify-between hover:border-slate-700 transition-colors">
              <div className="space-y-4">
                <span className="text-xs font-mono text-slate-400">CUSTOM SOFTWARE / ERP</span>
                <h3 className="text-xl font-bold text-white">Multi-Branch Web ERP</h3>
                <p className="text-2xl font-mono font-extrabold text-[#c59b4c]">KES 85,000+</p>
                <p className="text-xs text-slate-400">Customized according to operational scope</p>
                <ul className="space-y-2.5 text-xs text-slate-300 pt-2 border-t border-slate-800">
                  <li className="flex items-center gap-2">✓ Replaces Excel Worksheets &amp; Manual Books</li>
                  <li className="flex items-center gap-2">✓ Multi-User Staff Roles &amp; Audit Logs</li>
                  <li className="flex items-center gap-2">✓ Automated Cashier M-Pesa Reconciliation</li>
                  <li className="flex items-center gap-2">✓ KRA eTIMS API Fiscalization</li>
                  <li className="flex items-center gap-2">✓ Dedicated Technical SLA Support</li>
                </ul>
              </div>
              <button
                onClick={() => handleWhatsAppClick('Custom Web ERP Inquiry')}
                className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white font-bold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
              >
                <MessageSquare className="w-4 h-4 text-emerald-400" />
                <span>Consult Software Engineer</span>
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* 3. Interactive Regional Quote Estimator */}
      <section className="py-16 bg-[#091724] border-b border-slate-800">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <div className="space-y-2">
            <span className="text-xs font-mono text-[#fcd34d] uppercase font-semibold">
              INSTANT QUOTE ESTIMATOR · {hub.cityName.toUpperCase()}
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-serif-heading">
              Calculate Your Project Investment in 15 Seconds
            </h2>
          </div>

          <div className="bg-[#0d1622] rounded-3xl border border-slate-700 p-6 sm:p-8 text-left space-y-6">
            
            {/* System Type Selector */}
            <div className="space-y-2">
              <label className="text-xs font-mono text-slate-300 font-bold block">1. Select Required System Type:</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { id: 'website', label: 'Business Website' },
                  { id: 'app', label: 'Mobile App (Android/iOS)' },
                  { id: 'erp', label: 'Custom ERP / POS' },
                  { id: 'ai', label: 'WhatsApp AI Bot' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCalcService(item.id as any);
                      if (item.id === 'website') setCalcBudget(22000);
                      if (item.id === 'app') setCalcBudget(45000);
                      if (item.id === 'erp') setCalcBudget(85000);
                      if (item.id === 'ai') setCalcBudget(35000);
                    }}
                    className={`py-3 px-3 rounded-xl text-xs font-bold transition-all text-center border cursor-pointer ${
                      calcService === item.id
                        ? 'bg-[#c59b4c] text-slate-950 border-[#c59b4c]'
                        : 'bg-slate-900 text-slate-300 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Estimated Investment Range Output */}
            <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <span className="text-xs font-mono text-slate-400 block">Estimated Investment Range ({hub.cityName}):</span>
                <span className="text-2xl sm:text-3xl font-mono font-extrabold text-[#fcd34d]">
                  KES {calcBudget.toLocaleString()}
                </span>
                <span className="text-[11px] text-emerald-400 block mt-0.5">
                  ✓ Qualifies for Lipa Pole Pole (30% Deposit to Start)
                </span>
              </div>

              <button
                onClick={() => handleWhatsAppClick(`Estimated Quote: KES ${calcBudget.toLocaleString()}`, calcService.toUpperCase())}
                className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-lg"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Send Quote to WhatsApp</span>
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* 4. Local FAQ Accordion */}
      <section className="py-16 bg-[#080d11] border-b border-slate-800">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="text-center space-y-2">
            <span className="text-xs font-mono text-[#c59b4c] uppercase font-semibold">FREQUENTLY ASKED QUESTIONS</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-serif-heading">
              Got Questions About Web &amp; Software Development in {hub.cityName}?
            </h2>
          </div>

          <div className="space-y-3">
            {hub.localizedFaqs.map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-slate-800 bg-[#0d1622] overflow-hidden transition-colors"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                    className="w-full p-5 text-left font-bold text-sm sm:text-base text-white flex items-center justify-between gap-4 cursor-pointer"
                  >
                    <span>{faq.question}</span>
                    <ChevronDown className={`w-4 h-4 text-[#c59b4c] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5 text-xs sm:text-sm text-slate-300 leading-relaxed border-t border-slate-800/80 pt-3">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. Schema.org Multi-Location JSON-LD Injection */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ProfessionalService',
            'name': `Thinkers Arcade ${hub.cityName} Digital Office`,
            'url': `https://thinkersarcade.co.ke/kenya/${hub.key}/`,
            'telephone': '+254114646341',
            'email': 'deals@thinkersarcade.co.ke',
            'address': {
              '@type': 'PostalAddress',
              'addressLocality': hub.cityName,
              'addressRegion': hub.countyName,
              'addressCountry': 'KE',
            },
            'geo': {
              '@type': 'GeoCoordinates',
              'latitude': hub.mapCoordinates.lat,
              'longitude': hub.mapCoordinates.lng,
            },
            'priceRange': 'KES 9000 - KES 250000',
            'paymentAccepted': 'M-Pesa, Bank Transfer, Cash',
            'currenciesAccepted': 'KES',
          }),
        }}
      />

    </div>
  );
};
